import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { assetProcessingStates } from '@cube3/common/model/resource-types';
import { useCurrentWorkspace } from '@cube3/state/src/redux/components/Administration/withCurrentWorkspace';
// import { useCurrentAccount } from '@cube3/state/src/redux/components/Administration/withCurrentUserAccount';
import { useResourceList__ALPHA } from '@cube3/state/src/redux/components/Hooks/useResourceList';
import { Upload } from '@cube3/common/model/schema/resources/file-upload';
import { Asset } from '@cube3/common/model/schema/resources/asset';
import { useResource__ALPHA } from '@cube3/state/src/redux/components/Hooks/useResource';
import { Params } from '@cube3/state/src/wrapped-cube-client/params';

const DEFAULT_POLL_DURATION = 3000;

interface UseAssetPollConfig {
  uploads: Array<Upload | Asset>;
  folderId?: string;
  pollDuration?: number;
  idlePollDuration?: number;
  shouldPollFolder?: boolean;
  isShare?: boolean;
  defaultParams?: Params
}


export const useAssetsPoll = ({
  uploads,
  folderId,
  pollDuration = DEFAULT_POLL_DURATION,
  idlePollDuration = pollDuration,
  shouldPollFolder,
  isShare,
  defaultParams
}: UseAssetPollConfig) => {
  const [workspaceId] = useCurrentWorkspace();
  // const [accountId] = useCurrentAccount(true);

  // const dispatch = useDispatch();

  const assetIds = useMemo(() => {
    return uploads
      ?.filter(
        (u) =>
          u.type !== 'asset' ||
          ![assetProcessingStates.COMPLETE, assetProcessingStates.FAILED].find(
            (s) => s === (u as Asset).processing_status
          )
      )
      .map((u) => (u.type === 'asset' ? u.id : (u as Upload).asset_id))
      .filter((i) => !!i)
      .map((i) => ({ type: 'asset' as const, id: i }));
  }, [uploads]);

  const paramsPoll = useMemo(() => {
    return assetIds?.length
      ? {
        page: { size: -1 },
        filter: {
          ids: assetIds.map((a) => a.id).join(',')
        },
        sort: '-created_at'
      }
      : undefined;
  }, [assetIds]);

  const polledAssets = useResourceList__ALPHA({
    resourceType: folderId ? 'folder' : 'workspace',
    resourceId: folderId ? folderId : workspaceId,
    edgeType: paramsPoll ? 'asset' : undefined,
    edgeLabel: 'assets',
    params: paramsPoll || defaultParams,
    strategy: 'fetch-on-mount'
  });

  const sharePolledAssets = useResource__ALPHA({
    resourceIdentifiers: isShare ? assetIds : undefined,
    strategy: 'fetch-on-mount'
  });

  const folders = useResourceList__ALPHA({
    resourceType: 'folder',
    resourceId: folderId,
    edgeType: 'folder',
    edgeLabel: 'folders',
    strategy: 'prefer-cache'
  });

  const assets = useResourceList__ALPHA({
    resourceType: 'folder',
    resourceId: folderId,
    edgeType: 'asset',
    edgeLabel: 'assets',
    strategy: 'prefer-cache',
    params: defaultParams
  });

  // check if there are assets that haven't completely processed
  const unfinished =
    polledAssets.resources?.filter(
      (a) =>
        ![assetProcessingStates.COMPLETE, assetProcessingStates.FAILED].find(
          (s) => s === a.processing_status
        )
    ).length || 0;

  const busy = polledAssets.loading;
  const poll = polledAssets.retrieve;
  const sharePoll = sharePolledAssets.retrieve;
  const folderPoll = folders.retrieve;
  const assetPoll = assets.retrieve;
  const folderBusy = folders.loading;
  const assetBusy = assets.loading;

  const uploadPolledAt = useRef(0);
  const folderPolledAt = useRef(Date.now()); // prevent redundant initial folder reload

  const pollInterval = unfinished ? pollDuration : idlePollDuration / 2; // divided by two to allow both polling strategies an opportunity

  // We use a single polling interval to conditionally
  // perform one of multiple possible polling requests.
  // The callback is passed as a ref to the useEffect call,
  // so the interval doesn't get reset when deps change.
  // There are two polling "strategies"
  // - poll the list of assets provided via hook config
  // - poll the assets and subfolders inside the folderId provided via hook config

  // We use two timestamps to track when which request has
  // last taken place and use pollDuration and idlePollDuration
  // to determine whether they should be retriggered.
  // If shouldPollFolder is true, polling folder contents will run on the idlePollDuration interval.

  // If both uploads and folder content are due for an update, we try to prevent
  // both requests firing at the same time.
  // Folder contents will get priority in this case, cause the run less often and
  // usually cover the polled uploads data as well.
  const pollCallback = useCallback(() => {
    // use timestamps to determine wheter requests are due an update
    const now = Date.now();
    // if any request is still out, prevent spamming
    if (busy || folderBusy || assetBusy) {
      return;
    }
    // folder poll has priority
    if (shouldPollFolder && now - folderPolledAt.current > idlePollDuration) {
      folderPoll();
      assetPoll();
      folderPolledAt.current = now;
    } else {
      if (now - uploadPolledAt.current > pollDuration) {
        if (isShare) {
          sharePoll();
        } else {
          poll();
        }
        uploadPolledAt.current = now;
      }
    }
  }, [
    poll,
    busy,
    folderBusy,
    assetBusy,
    folderPoll,
    assetPoll,
    shouldPollFolder,
    pollDuration,
    idlePollDuration
  ]);

  // passing the callback directly would still cause the
  // interval to reset anytime a dep of the callback changes
  const pollCallbackRef = useRef(null);
  pollCallbackRef.current = pollCallback;

  // note the single dep to this hook, all the
  // other "deps" can change without resetting the interval.
  useEffect(() => {
    const interval = setInterval(() => {
      pollCallbackRef.current();
    }, pollInterval);
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [pollInterval]);

  return { polledAssets };
};

export const withAssetsPoll =
  (config = {}) =>
    (Wrapped) =>
      (props) => {
        useAssetsPoll({
          uploads: props.uploads || props.assets,
          ...config,
          ...props
        });

        return <Wrapped {...props} />;
      };
